import { action, computed, makeObservable, observable } from 'mobx';
import { WindowCollection } from './WindowCollection';
import { WindowState } from './WindowController';

export interface ISingleUnwrappedWindow<D extends Record<string, any> = {}> {
  active?: string;
  windows: Record<string, WindowState<D>>;
  wrap(id?: string): void;
  unwrap(id?: string): void;
  open(id: string, data: D): void;
  close(id?: string): void;
}

/** Сущность для управления множеством окон. Максимальное число видимых окон ограничено одним */
export class SingleUnwrappedWindow<D extends Record<string, any> = {}>
  implements ISingleUnwrappedWindow<D>
{
  @observable private _collection = new WindowCollection<D>();
  @observable private _active?: string = undefined;

  constructor() {
    makeObservable<SingleUnwrappedWindow<D>>(this);
  }

  /** Последнее незакрытое окно со состоянием opened */
  @computed get active(): string | undefined {
    return this._active;
  }

  @computed get windows(): Record<string, WindowState<D>> {
    const result: Record<string, WindowState<D>> = {};

    for (let [key, window] of this._collection.list.entries()) {
      const { data, visible } = window;

      result[key] = { data, visible };
    }

    return result;
  }

  @action wrap = (id?: string) => {
    const windowId = id || this._active;

    if (!windowId) return;

    this._collection.list.get(windowId)?.wrap();
  };

  @action unwrap = (id?: string) => {
    if (this._active) {
      this._collection.list.get(this._active)?.unwrap();
    }

    const windowId = id || this._active;

    if (!windowId) return;

    this._collection.list.get(windowId)?.unwrap();

    if (id) this._active = id;
  };

  @action open = (id: string, data: D) => {
    if (this._active) {
      this._collection.list.get(this._active)?.wrap();
    }

    this._collection.open(id, data);

    this._active = id;
  };

  @action close = (id?: string) => {
    const windowId = id || this._active;

    if (!windowId) return;

    this._collection.close(windowId);

    if (windowId === this._active) {
      this._active = Object.keys(this.windows)[0];
    }
  };
}
